import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NachaParserService } from '../../../services/nacha-parser.service';
import { NachaFileSummary } from '../../../models/nacha.model';

@Component({
  selector: 'app-nacha-upload-step',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './upload-step.component.html',
})
export class NachaUploadStepComponent implements OnInit {
  @Output() nachaUploaded = new EventEmitter<{ fileName: string; content: string }>();
  @Input() fileSummary: NachaFileSummary | null = null;
  @Input() parseErrors: string[] = [];
  // Restores the "file selected" display when this component is re-created
  // after navigating back to Step 1 (the wizard destroys/recreates it via
  // @if, so its own fileName would otherwise reset even though the parent
  // still holds parseResult/fileSummary and keeps "Next" enabled).
  @Input() initialFileName: string | null = null;

  uploadError: string | null = null;
  fileName: string | null = null;
  isDragging = false;
  isProcessing = false;

  constructor(private nachaParser: NachaParserService) {}

  ngOnInit(): void {
    if (this.initialFileName) {
      this.fileName = this.initialFileName;
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  private async handleFile(file: File): Promise<void> {
    this.uploadError = null;

    if (!file.name.toLowerCase().endsWith('.txt')) {
      this.uploadError = 'Please select a NACHA .txt file';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      this.uploadError = 'File size exceeds the maximum allowed size of 5MB';
      return;
    }

    this.isProcessing = true;
    this.fileName = file.name;

    try {
      const content = await this.nachaParser.readFileAsText(file);
      this.nachaUploaded.emit({ fileName: file.name, content });
    } catch (error: any) {
      this.uploadError = error.message || 'Failed to read the NACHA file';
      this.fileName = null;
    } finally {
      this.isProcessing = false;
    }
  }

  clearFile(): void {
    this.fileName = null;
    this.uploadError = null;
    this.nachaUploaded.emit({ fileName: '', content: '' });
  }

  get hasValidFile(): boolean {
    return !!this.fileName && !this.uploadError && (this.fileSummary?.entryCount ?? 0) > 0;
  }

  formatAmount(amount: number): string {
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
}
